/**
 * Creates a URL-friendly slug from a string
 * @param {string} text - The text to convert to a slug
 * @returns {string} The URL-friendly slug (max 75 characters)
 */
export const createSlug = (text) => {
  if (!text) return '';
  
  let slug = text
    .toString()
    .toLowerCase()
    .trim()
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove special characters
    .replace(/[^\w\-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/\-\-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '');
  
  // Limit to 75 characters for SEO and backend compatibility
  if (slug.length > 75) {
    // Truncate to 75 characters, but try to break at a word boundary
    slug = slug.substring(0, 75);
    
    // If the truncated slug ends with a hyphen, remove it
    slug = slug.replace(/-+$/, '');
    
    // If the slug is still longer than 75 chars after removing trailing hyphens,
    // truncate again and ensure no trailing hyphen
    if (slug.length > 75) {
      slug = slug.substring(0, 75).replace(/-+$/, '');
    }
  }
  
  return slug;
};

/**
 * Normalizes an existing slug to match backend format (75 characters)
 * @param {string} slug - Existing slug to normalize
 * @param {string} title - Original title for regeneration if needed
 * @returns {string} - Normalized slug
 */
export const normalizeSlug = (slug, title = '') => {
  if (!slug) {
    return title ? createSlug(title) : '';
  }
  
  // If slug is already 75 characters or less, return as is
  if (slug.length <= 75) {
    return slug;
  }
  
  // If slug is too long, regenerate from title
  if (title) {
    return createSlug(title);
  }
  
  // If no title provided, truncate the existing slug
  return slug.substring(0, 75).replace(/-+$/, '');
};

/**
 * Creates a consistent slug that matches backend format
 * @param {string} title - The title to convert to slug
 * @param {string} existingSlug - Existing slug (optional)
 * @returns {string} - Consistent 75-character slug
 */
export const createConsistentSlug = (title, existingSlug = '') => {
  // Always generate from title to ensure consistency
  return createSlug(title);
}; 